import { type FC } from 'react'
import { useIntl } from 'react-intl'

import { Link } from '@tanstack/react-router'

import { buttonVariants } from '@/components/atoms/Button/Button'
import { type JudicialRecord } from '@/components/model'
import { AdminHeader } from '@/components/organisms/AdminHeader'
import { cn } from '@/lib/utils'

import { volunteerPiloteUrl } from '../../utils/volunteerPiloteUrl'

export interface JudicialRecordViewProps {
  record: JudicialRecord
  onBackToPiloteClicked?: () => void | Promise<void>
  className?: string
}

export const JudicialRecordView: FC<JudicialRecordViewProps> = ({ record, onBackToPiloteClicked, className }) => {
  const intl = useIntl()

  return (
    <div className={cn('flex h-full flex-col', className)}>
      <AdminHeader onBackToPiloteClicked={onBackToPiloteClicked} />

      <div className="flex h-[calc(100vh-6rem)] flex-col pl-10">
        <div className="mb-4 flex items-baseline gap-4 p-6">
          <h1 className="text-xl font-normal text-gray-800 select-none">
            {intl.formatMessage({ id: 'judicial.results.pageTitle' })}
          </h1>
          <p className="text-sm">
            <a href={volunteerPiloteUrl(record)} target="_blank" className="text-blue-700 underline" rel="noreferrer">
              {record.volunteer.code}
            </a>
          </p>
          <p className="text-sm">
            {record.volunteer.lastName}, {record.volunteer.firstName}
          </p>
          <p className="text-sm">{record.volunteer.birthDate}</p>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Municipal Column */}
          <div className="flex flex-1 flex-col gap-6 overflow-hidden">
            <h2 className="text-2xl font-bold text-gray-800">
              {intl.formatMessage({ id: 'judicial.record.municipal.title' })}
            </h2>
            <div className="flex flex-1 flex-col gap-4 overflow-y-auto pr-6">
              {record.municipal?.values?.map((item, index) => (
                <div key={index}>
                  {index > 0 && <hr className="mb-4 border-gray-300" />}
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex flex-1 flex-col gap-2 text-sm text-gray-800">
                      <p>
                        <span className="font-semibold">
                          {intl.formatMessage({ id: 'judicial.record.municipal.fileNumber' })}:
                        </span>{' '}
                        {item.cleDossier}
                      </p>
                      <p>
                        <span className="font-semibold">
                          {intl.formatMessage({ id: 'judicial.record.municipal.name' })}:
                        </span>{' '}
                        {item.nomPrenom}
                      </p>
                      <p>
                        <span className="font-semibold">
                          {intl.formatMessage({ id: 'judicial.record.municipal.birthDate' })}:
                        </span>{' '}
                        {item.dateNaissance}
                      </p>
                      <p>
                        <span className="font-semibold">
                          {intl.formatMessage({ id: 'judicial.record.municipal.infractionDate' })}:
                        </span>{' '}
                        {item.dateInfraction}
                      </p>
                      <p>
                        <span className="font-semibold">
                          {intl.formatMessage({ id: 'judicial.record.municipal.court' })}:
                        </span>{' '}
                        {item.cour}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <Link
                        className={buttonVariants({ size: 'sm', variant: 'default' })}
                        from="/judicial/$recordId"
                        to="/judicial/$recordId/municipal/$soquijId"
                        params={{ soquijId: item.cleDossier }}
                        target="_blank">
                        {intl.formatMessage({ id: 'judicial.record.action.view' })}
                      </Link>
                      <p className="text-base text-red-700">
                        {intl.formatMessage({ id: 'judicial.record.feesApplicable' })}
                      </p>
                      {record.viewedResults?.[`municipal:${item.cleDossier}`] && (
                        <p className="text-sm text-gray-500">
                          {intl.formatMessage(
                            { id: 'judicial.record.viewedAt' },
                            {
                              date: intl.formatDate(record.viewedResults[`municipal:${item.cleDossier}`], {
                                dateStyle: 'medium',
                                timeStyle: 'short',
                              }),
                            }
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Vertical Divider */}
          <div className="h-auto w-px bg-gray-300" />

          {/* Provincial Column */}
          <div className="flex flex-1 flex-col gap-6 overflow-hidden pl-6">
            <h2 className="text-2xl font-bold text-gray-800">
              {intl.formatMessage({ id: 'judicial.record.provincial.title' })}
            </h2>
            <div className="flex flex-1 flex-col gap-4 overflow-y-auto pr-10">
              {record.provincial?.values?.map((item, index) => (
                <div key={index}>
                  {index > 0 && <hr className="mb-4 border-gray-300" />}
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex flex-1 flex-col gap-2 text-sm text-gray-800">
                      <p>
                        <span className="font-semibold">
                          {intl.formatMessage({ id: 'judicial.record.provincial.fileNumber' })}:
                        </span>{' '}
                        {item.cleDossier}
                      </p>
                      <p>
                        <span className="font-semibold">
                          {intl.formatMessage({ id: 'judicial.record.provincial.plaintiff' })}:
                        </span>{' '}
                        {item.demandeur}
                      </p>
                      <p>
                        <span className="font-semibold">
                          {intl.formatMessage({ id: 'judicial.record.provincial.defendant' })}:
                        </span>{' '}
                        {item.defendeur}
                      </p>
                      <p>
                        <span className="font-semibold">
                          {intl.formatMessage({ id: 'judicial.record.provincial.birthDate' })}:
                        </span>{' '}
                        {item.dateDeNaissance}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <Link
                        from="/judicial/$recordId"
                        to="/judicial/$recordId/provincial/$soquijId"
                        className={buttonVariants({ size: 'sm', variant: 'default' })}
                        params={{ soquijId: item.cleDossier }}
                        target="_blank">
                        {intl.formatMessage({ id: 'judicial.record.action.view' })}
                      </Link>
                      <p className="text-base text-red-700">
                        {intl.formatMessage({ id: 'judicial.record.feesApplicable' })}
                      </p>
                      {record.viewedResults?.[`provincial:${item.cleDossier}`] && (
                        <p className="text-sm text-gray-500">
                          {intl.formatMessage(
                            { id: 'judicial.record.viewedAt' },
                            {
                              date: intl.formatDate(record.viewedResults[`provincial:${item.cleDossier}`], {
                                dateStyle: 'medium',
                                timeStyle: 'short',
                              }),
                            }
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
